// One dynamic mesh per layer. Everything the game draws is an axis-aligned
// textured quad, so a layer is rebuilt by refilling three typed arrays.
import * as pc from "playcanvas";
import type { Frame } from "./atlas.ts";

export class QuadBatch {
  readonly entity: pc.Entity;
  private readonly mesh: pc.Mesh;
  private readonly instance: pc.MeshInstance;
  private readonly positions: Float32Array;
  private readonly uvs: Float32Array;
  private readonly colors: Float32Array;
  private readonly indices: Uint32Array;
  private readonly maxQuads: number;
  private count = 0;

  constructor(app: pc.AppBase, material: pc.Material, maxQuads: number, name: string) {
    this.maxQuads = maxQuads;
    this.positions = new Float32Array(maxQuads * 4 * 3);
    this.uvs = new Float32Array(maxQuads * 4 * 2);
    this.colors = new Float32Array(maxQuads * 4 * 4);
    this.indices = new Uint32Array(maxQuads * 6);
    for (let q = 0; q < maxQuads; q++) {
      const v = q * 4;
      const i = q * 6;
      this.indices[i] = v;
      this.indices[i + 1] = v + 1;
      this.indices[i + 2] = v + 2;
      this.indices[i + 3] = v;
      this.indices[i + 4] = v + 2;
      this.indices[i + 5] = v + 3;
    }

    this.mesh = new pc.Mesh(app.graphicsDevice);
    this.mesh.clear(true, true, maxQuads * 4, maxQuads * 6);

    this.instance = new pc.MeshInstance(this.mesh, material);
    this.instance.cull = false;
    this.instance.visible = false;

    this.entity = new pc.Entity(name);
    this.entity.addComponent("render", { meshInstances: [this.instance] });
  }

  begin(): void {
    this.count = 0;
  }

  /** How many quads have been pushed this frame. */
  get length(): number {
    return this.count;
  }

  /**
   * @param x left edge, world units
   * @param y bottom edge, world units
   * @param z depth; larger draws in front
   * @param w width in world units
   * @param h height in world units
   * @param tint multiplied into the texel colour
   * @param glow additive white on the opaque material, opacity on the FX one
   */
  add(
    x: number,
    y: number,
    z: number,
    w: number,
    h: number,
    f: Frame,
    tint: readonly [number, number, number] = WHITE,
    glow = 0,
  ): void {
    if (this.count >= this.maxQuads) return;
    const q = this.count++;
    const p = q * 12;
    const u = q * 8;
    const c = q * 16;
    const P = this.positions;
    const U = this.uvs;
    const C = this.colors;
    const x1 = x + w;
    const y1 = y + h;

    P[p] = x;      P[p + 1] = y;       P[p + 2] = z;
    P[p + 3] = x1; P[p + 4] = y;       P[p + 5] = z;
    P[p + 6] = x1; P[p + 7] = y1;      P[p + 8] = z;
    P[p + 9] = x;  P[p + 10] = y1;     P[p + 11] = z;

    U[u] = f.u0;     U[u + 1] = f.v1;
    U[u + 2] = f.u1; U[u + 3] = f.v1;
    U[u + 4] = f.u1; U[u + 5] = f.v0;
    U[u + 6] = f.u0; U[u + 7] = f.v0;

    for (let i = 0; i < 4; i++) {
      C[c + i * 4] = tint[0];
      C[c + i * 4 + 1] = tint[1];
      C[c + i * 4 + 2] = tint[2];
      C[c + i * 4 + 3] = glow;
    }
  }

  end(): void {
    if (this.count === 0) {
      this.instance.visible = false;
      return;
    }
    const verts = this.count * 4;
    this.mesh.setPositions(this.positions, 3, verts);
    this.mesh.setUvs(0, this.uvs, 2, verts);
    this.mesh.setColors(this.colors, 4, verts);
    this.mesh.setIndices(this.indices, this.count * 6);
    this.mesh.update(pc.PRIMITIVE_TRIANGLES, false);
    this.instance.visible = true;
  }
}

const WHITE: readonly [number, number, number] = [1, 1, 1];

export function createSpriteMaterial(texture: pc.Texture): pc.ShaderMaterial {
  const material = new pc.ShaderMaterial({
    uniqueName: "farmSprite",
    attributes: {
      aPosition: pc.SEMANTIC_POSITION,
      aUv0: pc.SEMANTIC_TEXCOORD0,
      aColor: pc.SEMANTIC_COLOR,
    },
    vertexGLSL: /* glsl */ `
      attribute vec3 aPosition;
      attribute vec2 aUv0;
      attribute vec4 aColor;

      uniform mat4 matrix_model;
      uniform mat4 matrix_viewProjection;

      varying vec2 vUv0;
      varying vec4 vColor;

      void main(void) {
        vUv0 = aUv0;
        vColor = aColor;
        gl_Position = matrix_viewProjection * matrix_model * vec4(aPosition, 1.0);
      }`,
    fragmentGLSL: /* glsl */ `
      precision highp float;

      uniform sampler2D uAtlas;
      uniform vec3 uDaylight;

      varying vec2 vUv0;
      varying vec4 vColor;

      void main(void) {
        vec4 texel = texture2D(uAtlas, vUv0);
        if (texel.a < 0.5) discard;
        vec3 rgb = texel.rgb * vColor.rgb * uDaylight;
        rgb += vec3(vColor.a) * 0.55;
        gl_FragColor = vec4(clamp(rgb, 0.0, 1.0), 1.0);
      }`,
  });
  material.cull = pc.CULLFACE_NONE;
  material.depthTest = true;
  material.depthWrite = true;
  material.blendType = pc.BLEND_NONE;
  material.setParameter("uAtlas", texture);
  material.setParameter("uDaylight", [1, 1, 1]);
  material.update();
  return material;
}

/**
 * The one blended material. Everything else in the game is alpha-tested with no
 * blending, which is what makes depth sorting free; anything translucent — rain,
 * soft shadows, sparkles, lamp glow — has to live here instead. It still depth
 * *tests* against the world so a puff behind a tree stays behind it, but it never
 * writes depth, and having a blend mode at all puts it in PlayCanvas's transparent
 * bucket, which is drawn after every opaque layer.
 *
 * Vertex alpha means opacity here, not the additive glow the opaque shader uses.
 */
export function createFxMaterial(texture: pc.Texture): pc.ShaderMaterial {
  const material = new pc.ShaderMaterial({
    uniqueName: "farmSpriteFx",
    attributes: {
      aPosition: pc.SEMANTIC_POSITION,
      aUv0: pc.SEMANTIC_TEXCOORD0,
      aColor: pc.SEMANTIC_COLOR,
    },
    vertexGLSL: /* glsl */ `
      attribute vec3 aPosition;
      attribute vec2 aUv0;
      attribute vec4 aColor;

      uniform mat4 matrix_model;
      uniform mat4 matrix_viewProjection;

      varying vec2 vUv0;
      varying vec4 vColor;

      void main(void) {
        vUv0 = aUv0;
        vColor = aColor;
        gl_Position = matrix_viewProjection * matrix_model * vec4(aPosition, 1.0);
      }`,
    fragmentGLSL: /* glsl */ `
      precision highp float;

      uniform sampler2D uAtlas;
      uniform vec3 uDaylight;

      varying vec2 vUv0;
      varying vec4 vColor;

      void main(void) {
        vec4 texel = texture2D(uAtlas, vUv0);
        float a = texel.a * vColor.a;
        if (a < 0.01) discard;
        gl_FragColor = vec4(texel.rgb * vColor.rgb * uDaylight, a);
      }`,
  });
  material.cull = pc.CULLFACE_NONE;
  material.depthTest = true;
  material.depthWrite = false;
  material.blendType = pc.BLEND_NORMAL;
  material.setParameter("uAtlas", texture);
  material.setParameter("uDaylight", [1, 1, 1]);
  material.update();
  return material;
}
